import React, { useState, useEffect } from 'react';
import { MapPin, Star, ArrowRight, Loader2, ChevronLeft, ChevronRight, Clock, Calendar, MessageCircle, Tag, Utensils, Building2, ArrowLeft } from 'lucide-react';

const COUNTRY_MAP: Record<number, { name: string; flag: string }> = {
  1: { name: 'Египет', flag: '🇪🇬' }, 2: { name: 'Таиланд', flag: '🇹🇭' },
  4: { name: 'Турция', flag: '🇹🇷' }, 7: { name: 'Индонезия', flag: '🇮🇩' },
  8: { name: 'Мальдивы', flag: '🇲🇻' }, 9: { name: 'ОАЭ', flag: '🇦🇪' },
  10: { name: 'Куба', flag: '🇨🇺' }, 12: { name: 'Шри-Ланка', flag: '🇱🇰' },
  13: { name: 'Китай', flag: '🇨🇳' }, 16: { name: 'Вьетнам', flag: '🇻🇳' },
  41: { name: 'Танзания', flag: '🇹🇿' }, 28: { name: 'Сейшелы', flag: '🇸🇨' },
  47: { name: 'Россия', flag: '🇷🇺' }, 21: { name: 'Черногория', flag: '🇲🇪' },
};

const MEAL_MAP: Record<number, string> = {
  1: 'RO', 2: 'RO', 3: 'BB', 4: 'HB', 5: 'FB', 6: 'UAI', 7: 'AI',
};

interface HotTour {
  id: number; tour_id: number; country_id: number; hotel_id: number;
  fly_date: string; nights: number; meal_id: number; meal_group_id: number;
  stars: number; old_price: number; price: number; title: string;
  description: string; picture: { path: string } | null;
}

interface CountryGroup {
  countryId: number; name: string; flag: string;
  minPrice: number; tourCount: number; tours: HotTour[];
  coverImage: string;
}

const fmtPrice = (n: number) => Math.round(n).toLocaleString('ru-RU');
const fmtDate = (s: string) => new Date(s).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });

const PopularDestinations: React.FC = () => {
  const [countries, setCountries] = useState<CountryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<CountryGroup | null>(null);
  const [selectedTour, setSelectedTour] = useState<HotTour | null>(null);
  const [photoIndex, setPhotoIndex] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch('/api/hot-tours');
        const data = await res.json();
        const items: HotTour[] = data.items || data || [];

        const map = new Map<number, CountryGroup>();
        for (const t of items) {
          const ci = COUNTRY_MAP[t.country_id];
          if (!ci) continue;
          const existing = map.get(t.country_id);
          if (!existing) {
            map.set(t.country_id, {
              countryId: t.country_id, name: ci.name, flag: ci.flag,
              minPrice: t.price, tourCount: 1, tours: [t],
              coverImage: t.picture?.path || '',
            });
          } else {
            existing.tours.push(t);
            existing.tourCount++;
            if (t.price < existing.minPrice) existing.minPrice = t.price;
            if (!existing.coverImage && t.picture?.path) existing.coverImage = t.picture.path;
          }
        }
        const sorted = Array.from(map.values()).sort((a, b) => b.tourCount - a.tourCount);
        setCountries(sorted);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  /* ── Tour Detail ── */
  if (selectedTour) {
    const t = selectedTour;
    const discount = t.old_price > 0 ? Math.round((1 - t.price / t.old_price) * 100) : 0;
    const meal = MEAL_MAP[t.meal_id] || MEAL_MAP[t.meal_group_id] || '';
    const countryInfo = COUNTRY_MAP[t.country_id];
    const img = t.picture?.path;

    return (
      <section id="tours" className="py-20 bg-neutral-50">
        <div className="container-custom">
          <button onClick={() => setSelectedTour(null)} className="flex items-center gap-1.5 text-primary-500 hover:text-primary-600 font-medium text-sm mb-6 group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Назад к {selectedCountry?.name || 'направлениям'}
          </button>

          <div className="bg-white rounded-2xl shadow-xl border border-neutral-100 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {img && (
                <div className="relative h-64 lg:h-auto lg:min-h-[450px]">
                  <img src={img} alt={t.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  {discount > 0 && (
                    <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1.5 rounded-full flex items-center font-bold text-sm shadow-lg">
                      <Tag className="w-3.5 h-3.5 mr-1" />-{discount}%
                    </div>
                  )}
                  {t.stars > 0 && (
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur rounded-lg px-2.5 py-1 flex gap-0.5">
                      {[...Array(t.stars)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />)}
                    </div>
                  )}
                </div>
              )}

              <div className="p-6 lg:p-8">
                <div className="text-sm text-neutral-400 mb-1">{countryInfo?.flag} {countryInfo?.name}</div>
                <h2 className="text-xl lg:text-2xl font-bold text-neutral-800 mb-4">{t.title}</h2>

                {t.description && (
                  <p className="text-neutral-500 text-sm leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: t.description.replace(/<br\s*\/?>/g, ' ') }} />
                )}

                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="bg-neutral-50 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 text-[11px] text-neutral-400 mb-0.5"><Calendar className="w-3 h-3" />Вылет</div>
                    <div className="text-sm font-medium text-neutral-700">{fmtDate(t.fly_date)}</div>
                  </div>
                  <div className="bg-neutral-50 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 text-[11px] text-neutral-400 mb-0.5"><Clock className="w-3 h-3" />Длительность</div>
                    <div className="text-sm font-medium text-neutral-700">{t.nights} ночей</div>
                  </div>
                  <div className="bg-neutral-50 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 text-[11px] text-neutral-400 mb-0.5"><Utensils className="w-3 h-3" />Питание</div>
                    <div className="text-sm font-medium text-neutral-700">{meal || '—'}</div>
                  </div>
                  <div className="bg-neutral-50 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 text-[11px] text-neutral-400 mb-0.5"><Building2 className="w-3 h-3" />Отель</div>
                    <div className="text-sm font-medium text-neutral-700">{t.stars > 0 ? `${t.stars}★` : '—'}</div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-secondary-300/10 to-secondary-400/10 rounded-xl p-5 mb-5">
                  <div className="flex items-baseline gap-3">
                    <div className="text-3xl font-bold text-secondary-300">{fmtPrice(t.price)} ₸</div>
                    {t.old_price > 0 && t.old_price !== t.price && (
                      <div className="text-sm text-neutral-400 line-through">{fmtPrice(t.old_price)} ₸</div>
                    )}
                  </div>
                  {discount > 0 && <div className="text-xs text-green-600 font-medium mt-1">Экономия {fmtPrice(t.old_price - t.price)} ₸</div>}
                </div>

                <a href={`https://wa.me/77019823126?text=${encodeURIComponent(`Здравствуйте! Интересует тур:\n${t.title}\nВылет: ${fmtDate(t.fly_date)}, ${t.nights} ночей\nЦена: ${fmtPrice(t.price)} ₸`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="btn bg-green-500 text-white hover:bg-green-600 w-full justify-center py-3.5 shadow-lg shadow-green-500/20">
                  <MessageCircle className="w-5 h-5 mr-2" /> Забронировать · {fmtPrice(t.price)} ₸
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* ── Country Tours List ── */
  if (selectedCountry) {
    const tours = selectedCountry.tours.sort((a, b) => a.price - b.price);

    return (
      <section id="tours" className="py-20 bg-neutral-50">
        <div className="container-custom">
          <button onClick={() => setSelectedCountry(null)} className="flex items-center gap-1.5 text-primary-500 hover:text-primary-600 font-medium text-sm mb-6 group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Все направления
          </button>

          <div className="flex items-center gap-3 mb-8">
            <span className="text-4xl">{selectedCountry.flag}</span>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-neutral-800">{selectedCountry.name}</h2>
              <p className="text-neutral-500 text-sm">{tours.length} горящих предложений из Алматы</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {tours.map((tour) => {
              const discount = tour.old_price > 0 ? Math.round((1 - tour.price / tour.old_price) * 100) : 0;
              const meal = MEAL_MAP[tour.meal_id] || MEAL_MAP[tour.meal_group_id] || '';
              return (
                <div key={tour.id} className="bg-white rounded-xl overflow-hidden border border-neutral-100 hover:shadow-xl transition-all group cursor-pointer"
                  onClick={() => { setSelectedTour(tour); document.getElementById('tours')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  <div className="relative h-44 overflow-hidden">
                    {tour.picture?.path ? (
                      <img src={tour.picture.path} alt={tour.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                        <MapPin className="w-10 h-10 text-primary-300" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    {discount > 0 && (
                      <div className="absolute top-3 left-3 bg-red-500 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow">-{discount}%</div>
                    )}
                    {tour.stars > 0 && (
                      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur rounded-md px-1.5 py-0.5 flex gap-0.5">
                        {[...Array(tour.stars)].map((_, i) => <Star key={i} className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400" />)}
                      </div>
                    )}
                    <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity bg-white text-primary-500 text-[11px] font-semibold px-2.5 py-1 rounded-lg shadow flex items-center gap-1">
                      Подробнее <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-neutral-800 text-sm leading-snug mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">{tour.title}</h3>
                    <div className="flex flex-wrap gap-1.5 text-[11px] mb-3">
                      <span className="bg-neutral-50 text-neutral-500 px-2 py-0.5 rounded">{fmtDate(tour.fly_date)}</span>
                      <span className="bg-neutral-50 text-neutral-500 px-2 py-0.5 rounded">{tour.nights} ночей</span>
                      {meal && <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded">{meal}</span>}
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        {tour.old_price > 0 && tour.old_price !== tour.price && (
                          <div className="text-[11px] text-neutral-400 line-through">{fmtPrice(tour.old_price)} ₸</div>
                        )}
                        <div className="text-lg font-bold text-secondary-300">{fmtPrice(tour.price)} ₸</div>
                      </div>
                      <a href={`https://wa.me/77019823126?text=${encodeURIComponent(`Интересует: ${tour.title}, ${fmtPrice(tour.price)} ₸`)}`}
                        target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        className="flex items-center gap-1 bg-green-500 text-white text-[11px] font-medium px-2.5 py-1.5 rounded-lg hover:bg-green-600 transition-colors">
                        <MessageCircle className="w-3 h-3" /> Забронировать
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  /* ── Countries Grid ── */
  if (loading) {
    return (
      <section id="tours" className="py-20 bg-neutral-50">
        <div className="container-custom text-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-3" />
          <p className="text-neutral-400 text-sm">Загружаем направления...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="tours" className="py-20 bg-neutral-50">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
          <div>
            <div className="inline-block bg-primary-500/10 text-primary-500 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              Направления
            </div>
            <h2 className="section-title">Популярные направления</h2>
            <p className="text-neutral-500 max-w-2xl">
              Реальные предложения от туроператоров. Цены обновляются автоматически.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {countries.map((c) => (
            <div key={c.countryId}
              className="group cursor-pointer rounded-2xl overflow-hidden relative h-48 md:h-64 shadow-md hover:shadow-2xl transition-all hover:-translate-y-1"
              onClick={() => { setSelectedCountry(c); document.getElementById('tours')?.scrollIntoView({ behavior: 'smooth' }); }}>
              {c.coverImage ? (
                <img src={c.coverImage} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary-400 to-primary-600" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

              {/* Content */}
              <div className="absolute inset-0 p-4 md:p-5 flex flex-col justify-end">
                <div className="text-2xl md:text-3xl mb-1">{c.flag}</div>
                <h3 className="text-white font-bold text-lg md:text-xl leading-tight">{c.name}</h3>
                <div className="flex items-baseline gap-1.5 mt-1">
                  <span className="text-white/60 text-xs">от</span>
                  <span className="text-secondary-300 font-bold text-lg">{fmtPrice(c.minPrice)} ₸</span>
                </div>
                <div className="text-white/50 text-xs mt-1">{c.tourCount} предложений</div>
              </div>

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-primary-500/0 group-hover:bg-primary-500/20 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-primary-500 font-semibold text-sm px-4 py-2 rounded-xl shadow-lg flex items-center gap-1.5">
                  Смотреть туры <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PopularDestinations;
